import React, { useEffect, useState } from 'react';
import {
  Modal,
  Input,
  message,
  Button,
  DatePicker,
  Select,
  Form,
  Upload,
} from 'antd';
import { useTranslation } from '@refinedev/core';
import useApi, { useGet } from '@/api/useApi';
import { endPoints } from '@/api/configs';
import dayjs from 'dayjs';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import type { RcFile } from 'antd/es/upload';
import { getFileUrl } from '@/Helpers';
import { TopicsDataGet } from '@/api/types/topicsDataGet';

interface EditPostModalProps {
  open: boolean;
  onCancel: () => void;
  refetch: () => void;
  postId: string;
  initialValues: {
    title: string;
    view_period: string;
    location: string;
    type: string;
    date: string;
    summary: string;
    topics: number[];
    banner_id?: number;
    main_audio_id: number;
    main_video_id: number;
    media_ids?: number[];
  };
}

interface UploadState {
  fileList: UploadFile[];
  uploadId: string | null;
}

export const EditPostModal: React.FC<EditPostModalProps> = ({
  open,
  onCancel,
  refetch,
  postId,
  initialValues,
}) => {
  const { translate } = useTranslation();
  const { usePatch, usePost } = useApi();
  const { mutate: updatePost, isLoading } = usePatch(
    endPoints.adminPostEdit(postId)
  );
  const [form] = Form.useForm();
  const { data: topics, isLoading: isTopicsLoading } = useGet<TopicsDataGet[]>(
    endPoints.topicAdmin,
    endPoints.topicAdmin
  );
  // File upload states
  const [bannerUpload, setBannerUpload] = useState<UploadState>({
    fileList: [],
    uploadId: null,
  });
  const [audioUpload, setAudioUpload] = useState<UploadState>({
    fileList: [],
    uploadId: null,
  });
  const [videoUpload, setVideoUpload] = useState<UploadState>({
    fileList: [],
    uploadId: null,
  });

  // Multiple media uploads state

  // Upload mutation
  const { mutate: uploadFile, isLoading: isUploading } = usePost<
    { id: string },
    FormData
  >(endPoints.upload(), {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  useEffect(() => {
    if (open && initialValues) {
      form.setFieldsValue({
        ...initialValues,
        date: dayjs(initialValues.date),
      });

      // Reset upload states
      setBannerUpload({
        fileList: initialValues.banner_id
          ? [
              {
                uid: '-1',
                name: 'Current Banner',
                status: 'done',
                url: getFileUrl(initialValues.banner_id.toString()),
              },
            ]
          : [],
        uploadId: initialValues.banner_id
          ? initialValues.banner_id.toString()
          : null,
      });

      if (initialValues.main_audio_id) {
        setAudioUpload({
          fileList: [
            {
              uid: '-1',
              name: 'Current Audio',
              status: 'done',
              url: getFileUrl(initialValues.main_audio_id.toString()),
            },
          ],
          uploadId: initialValues.main_audio_id.toString(),
        });
      } else {
        setAudioUpload({
          fileList: [],
          uploadId: null,
        });
      }

      if (initialValues.main_video_id) {
        setVideoUpload({
          fileList: [
            {
              uid: '-1',
              name: 'Current Video',
              status: 'done',
              url: getFileUrl(initialValues.main_video_id.toString()),
            },
          ],
          uploadId: initialValues.main_video_id.toString(),
        });
      } else {
        setVideoUpload({
          fileList: [],
          uploadId: null,
        });
      }
    }
  }, [open, initialValues, form]);

  type MediaSlot = 'banner' | 'audio' | 'video';

  // Native file-picker filters. `accept` is only a hint — the picker lets the
  // user switch back to "All files" — so beforeMediaUpload re-checks the MIME
  // type and drops anything that does not belong in the slot.
  const MEDIA_ACCEPT: Record<MediaSlot, string> = {
    banner: 'image/*',
    audio: 'audio/*,video/*',
    video: 'video/*',
  };

  const isAllowedMediaFile = (type: MediaSlot, mimeType: string): boolean => {
    switch (type) {
      case 'banner':
        return mimeType.startsWith('image/');
      // Podcast episodes are routinely delivered as webm/mp4 and played for
      // their audio track, so the audio slot accepts video containers too.
      case 'audio':
        return mimeType.startsWith('audio/') || mimeType.startsWith('video/');
      case 'video':
        return mimeType.startsWith('video/');
    }
  };

  // Method to reset upload ID when choosing a new file
  const resetUpload = (type: 'banner' | 'audio' | 'video') => {
    switch (type) {
      case 'banner':
        setBannerUpload((prev) => ({ ...prev, uploadId: null }));
        break;
      case 'audio':
        setAudioUpload((prev) => ({ ...prev, uploadId: null }));
        break;
      case 'video':
        setVideoUpload((prev) => ({ ...prev, uploadId: null }));
        break;
    }
  };

  // Reject a file that does not match the slot it was selected for, before it
  // ever reaches the file list. Returning false keeps antd from auto-uploading.
  const beforeMediaUpload = (type: MediaSlot, file: RcFile) => {
    if (!isAllowedMediaFile(type, file.type)) {
      message.error(translate(`posts.invalid-${type}-type`));
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  // Handle file list changes
  const handleFileChange = (
    type: 'banner' | 'audio' | 'video',
    info: { fileList: UploadFile[] }
  ) => {
    const { fileList } = info;

    // If a new file is being added (has originFileObj), reset the uploadId
    if (fileList.length > 0 && fileList[0].originFileObj) {
      resetUpload(type);
    }

    switch (type) {
      case 'banner':
        setBannerUpload((prev) => ({ ...prev, fileList }));
        break;
      case 'audio':
        setAudioUpload((prev) => ({ ...prev, fileList }));
        break;
      case 'video':
        setVideoUpload((prev) => ({ ...prev, fileList }));
        break;
    }
  };

  // Handle file upload
  const handleUpload = (type: 'banner' | 'audio' | 'video') => {
    let fileList: UploadFile[] = [];

    switch (type) {
      case 'banner':
        fileList = bannerUpload.fileList;
        break;
      case 'audio':
        fileList = audioUpload.fileList;
        break;
      case 'video':
        fileList = videoUpload.fileList;
        break;
    }

    if (fileList.length > 0 && fileList[0].originFileObj) {
      const formData = new FormData();
      formData.append('file', fileList[0].originFileObj);

      uploadFile(formData, {
        onSuccess: (response) => {
          const uploadId = response.data.data.id;

          switch (type) {
            case 'banner':
              setBannerUpload((prev) => ({ ...prev, uploadId }));
              form.setFieldsValue({ banner_id: Number(uploadId) });
              break;
            case 'audio':
              setAudioUpload((prev) => ({ ...prev, uploadId }));
              form.setFieldsValue({ main_audio_id: Number(uploadId) });
              break;
            case 'video':
              setVideoUpload((prev) => ({ ...prev, uploadId }));
              form.setFieldsValue({ main_video_id: Number(uploadId) });
              break;
          }

          message.success(`${type} ${translate('posts.upload-success')}`);
        },
        onError: (error) => {
          message.error(error.message);
        },
      });
    }
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    const { banner_id: formBannerId, ...postValues } = values;
    const bannerId = bannerUpload.uploadId
      ? Number(bannerUpload.uploadId)
      : formBannerId;

    // Prepare update data with uploaded file IDs
    const updateData = {
      ...postValues,
      date: values.date.format('YYYY-MM-DD'),
      ...(bannerId ? { banner_id: bannerId } : {}),
      main_audio_id: audioUpload.uploadId
        ? Number(audioUpload.uploadId)
        : values.main_audio_id,
      main_video_id: videoUpload.uploadId
        ? Number(videoUpload.uploadId)
        : values.main_video_id,
    };

    updatePost(updateData, {
      onSuccess: () => {
        form.resetFields();
        message.success(translate('posts.success.edit-post'));
        refetch();
        onCancel();
      },
      onError: (error) => {
        message.error(error.message);
      },
    });
  };

  const handleCancel = () => {
    form.resetFields();
    // Reset upload states
    setBannerUpload({ fileList: [], uploadId: null });
    setAudioUpload({ fileList: [], uploadId: null });
    setVideoUpload({ fileList: [], uploadId: null });
    onCancel();
  };

  return (
    <Modal
      title={translate('posts.edit-post')}
      open={open}
      footer={null}
      onClose={handleCancel}
      onCancel={handleCancel}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleOk}
        initialValues={initialValues}
      >
        <Form.Item
          name="title"
          label={translate('posts.title')}
          rules={[
            { required: true, message: translate('pages.errors.required') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="view_period"
          label={translate('posts.viewPeriod')}
          rules={[
            { required: true, message: translate('pages.errors.required') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="location"
          label={translate('posts.location')}
          rules={[
            { required: true, message: translate('pages.errors.required') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="type"
          label={translate('posts.type')}
          rules={[
            { required: true, message: translate('pages.errors.required') },
          ]}
        >
          <Select>
            <Select.Option value="DIGITAL_REPORT">
              {translate('posts.digital-report')}
            </Select.Option>
            <Select.Option value="DOCUMENTARY">
              {translate('posts.documentary')}
            </Select.Option>
            <Select.Option value="PROGRAM">
              {translate('posts.program')}
            </Select.Option>
            <Select.Option value="PODCAST">
              {translate('posts.podcast')}
            </Select.Option>
            <Select.Option value="VIDEO">
              {translate('posts.video')}
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="date"
          label={translate('posts.date')}
          rules={[
            { required: true, message: translate('pages.errors.required') },
          ]}
          getValueProps={(value) => ({ value: value ? dayjs(value) : '' })}
        >
          <DatePicker format={'YYYY-MM-DD'} className="w-full" />
        </Form.Item>

        <Form.Item
          name="summary"
          label={translate('posts.summary')}
          rules={[
            { required: true, message: translate('pages.errors.required') },
          ]}
        >
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.Item
          name="topics"
          label={translate('posts.topics')}
          rules={[
            { required: true, message: translate('pages.errors.required') },
          ]}
        >
          <Select
            mode="multiple"
            options={topics?.data.data.map((topic) => ({
              label: topic.name,
              value: topic.id,
            }))}
            loading={isTopicsLoading}
            disabled={isTopicsLoading}
          />
        </Form.Item>

        <Form.Item
          name="banner_id"
          label={translate('posts.banner')}
          dependencies={['type']}
          rules={[
            ({ getFieldValue }) => ({
              required: getFieldValue('type') !== 'VIDEO',
              message: translate('pages.errors.required'),
            }),
          ]}
        >
          <div className="flex flex-wrap items-center gap-2">
            <Upload
              listType="picture"
              maxCount={1}
              accept={MEDIA_ACCEPT.banner}
              fileList={bannerUpload.fileList}
              onChange={(info) => handleFileChange('banner', info)}
              beforeUpload={(file) => beforeMediaUpload('banner', file)}
              className="w-full"
            >
              <Button icon={<UploadOutlined />}>
                {bannerUpload.uploadId
                  ? translate('posts.replace-file')
                  : translate('posts.select-file')}
              </Button>
            </Upload>
            {bannerUpload.fileList.length > 0 &&
              bannerUpload.fileList[0].originFileObj && (
                <Button
                  type="primary"
                  onClick={() => handleUpload('banner')}
                  loading={isUploading}
                >
                  {translate('posts.upload')}
                </Button>
              )}
            <Input type="number" hidden />
          </div>
        </Form.Item>

        {initialValues.main_audio_id ? (
          <Form.Item
            name="main_audio_id"
            label={translate('posts.mainAudio')}
            rules={[
              { required: true, message: translate('pages.errors.required') },
            ]}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Upload
                listType="text"
                maxCount={1}
                accept={MEDIA_ACCEPT.audio}
                fileList={audioUpload.fileList}
                onChange={(info) => handleFileChange('audio', info)}
                beforeUpload={(file) => beforeMediaUpload('audio', file)}
                className="w-full"
              >
                <Button icon={<UploadOutlined />}>
                  {audioUpload.uploadId
                    ? translate('posts.replace-file')
                    : translate('posts.select-file')}
                </Button>
              </Upload>
              {audioUpload.fileList.length > 0 &&
                audioUpload.fileList[0].originFileObj && (
                  <Button
                    type="primary"
                    onClick={() => handleUpload('audio')}
                    loading={isUploading}
                  >
                    {translate('posts.upload')}
                  </Button>
                )}
              <Input type="number" hidden />
            </div>
          </Form.Item>
        ) : null}

        {initialValues.main_video_id ? (
          <Form.Item
            name="main_video_id"
            label={translate('posts.mainVideo')}
            rules={[
              { required: true, message: translate('pages.errors.required') },
            ]}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Upload
                listType="text"
                maxCount={1}
                accept={MEDIA_ACCEPT.video}
                fileList={videoUpload.fileList}
                onChange={(info) => handleFileChange('video', info)}
                beforeUpload={(file) => beforeMediaUpload('video', file)}
                className="w-full"
              >
                <Button icon={<UploadOutlined />}>
                  {videoUpload.uploadId
                    ? translate('posts.replace-file')
                    : translate('posts.select-file')}
                </Button>
              </Upload>
              {videoUpload.fileList.length > 0 &&
                videoUpload.fileList[0].originFileObj && (
                  <Button
                    type="primary"
                    onClick={() => handleUpload('video')}
                    loading={isUploading}
                  >
                    {translate('posts.upload')}
                  </Button>
                )}
              <Input type="number" hidden />
            </div>
          </Form.Item>
        ) : null}
        <div className="flex justify-end gap-2 w-full">
          <Button type="default" onClick={handleCancel}>
            {translate('buttons.cancel')}
          </Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {translate('buttons.save')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
